import PropTypes from 'prop-types';
import { FLOW_NAVIGATION_ACTIONS, FLOW_SCREEN_TYPES } from '../../../../../flows/DynamicFlow/utilities/constants';
import * as ADDONS_COPY from '../../../../../pages/BirthdayPartyAddOns/utilities/copy';
import * as FOOD_DRINKS_COPY from '../../../../../pages/BirthdayPartyFoodAndDrinks/utilities/copy';
import { isArrayEmpty } from '../../../../../utilities/arraysUtils';
import { getCurrencyCodeText } from '../../../../../utilities/planUtils';
import SelectPackageQuantity from '../../../../Packages/SelectPackageQuantity';
import { Box, Text, TextLink } from '../../../../ui-kit';
import { editCopyByReplaceString, pluralizeWordsInCopy } from '../../../../utilities/copyFunctions';
import { PLURALIZE_PACKAGE_NAME_WORDS } from '../../../utilities/constants';
import { SummaryContent } from '../../Content';
import { ItemListWrapper } from '../assets/styles';
import * as COPY from '../utilities/copy';

const propTypes = {
  /** String date object with the checkin date */
  checkinDateSelection: PropTypes.string.isRequired,

  /** String date object with the checkout date */
  checkoutDateSelection: PropTypes.string.isRequired,

  /** Currenty selected packages */
  currentSelectedPackages: PropTypes.arrayOf(
    PropTypes.shape({
      packageCode: PropTypes.string,
      packageName: PropTypes.string,
      currencyCode: PropTypes.string,
      quantity: PropTypes.number,
      amount: PropTypes.number,
      showQuantitySelect: PropTypes.bool
    })
  ),

  /** Number of guests for the reservation */
  numberOfGuests: PropTypes.number.isRequired,

  /** Flag to indicate the property is in Niagara */
  isNiagaraProperty: PropTypes.bool,

  /** Function to update the package quantity */
  upsertPackage: PropTypes.func,

  /** Funtion to delete a package */
  deletePackage: PropTypes.func,

  /** Flag to indicate if the reservation has selected packages */
  hasSelectedPackages: PropTypes.bool
};

const getPackagePriceDisplay = packageItem =>
  `${(parseInt(packageItem.quantity || 1) * packageItem.amount).toFixed(2)}`;

const BIRTHDAY_SECTION_TO_SCREEN_TYPE = {
  [ADDONS_COPY.PAGE_TITLE]: FLOW_SCREEN_TYPES.PACKAGES_ADDONS,
  [FOOD_DRINKS_COPY.TITLE]: FLOW_SCREEN_TYPES.FOOD_DRINKS
};

const ListOfPackages = ({
  currentSelectedPackages,
  deletePackage,
  isNiagaraProperty,
  checkinDateSelection,
  checkoutDateSelection,
  numberOfGuests,
  updatePackageQuantity,
  allowPackageUpdate,
  isBirthdayPackage,
  isFoodAndDrinks
}) => {
  return (
    <ItemListWrapper allowPackageUpdate={allowPackageUpdate}>
      {currentSelectedPackages.map(item => {
        const packageName = item.packageTitle || item.title;

        // Only the packages showing their quantity need their name to match that quantity
        const packageNameToDisplay = item.quantity
          ? pluralizeWordsInCopy(packageName, item.quantity, PLURALIZE_PACKAGE_NAME_WORDS)
          : packageName;

        return (
          <SummaryContent.Item key={`packageCode-${item.packageCode}${item.isExtra ? '-extra' : ''}`}>
            <SummaryContent.Description lineHeight={'18px'}>
              {item.quantity ? `${item.quantity} ` : ''} {packageNameToDisplay}
            </SummaryContent.Description>
            {allowPackageUpdate && item.quantity && !item.packageType?.includes('dining') ? (
              <Box width="80px" style={{ textAlign: 'right' }}>
                <SelectPackageQuantity
                  isNiagaraProperty={isNiagaraProperty}
                  checkinDateSelection={checkinDateSelection}
                  checkoutDateSelection={checkoutDateSelection}
                  packageItem={item}
                  numberOfGuests={numberOfGuests}
                  updatePackageQuantity={updatePackageQuantity}
                />
              </Box>
            ) : null}
            {allowPackageUpdate ? (
              <SummaryContent.Action onClick={() => deletePackage(item)}>{COPY.REMOVE}</SummaryContent.Action>
            ) : null}

            <SummaryContent.Amount width={isBirthdayPackage && !item.isExtra ? 'auto' : 'initial'} fontSize={0}>
              {isBirthdayPackage && isFoodAndDrinks && !item.isExtra
                ? COPY.SUMMARY_INCLUDED
                : `$${getPackagePriceDisplay(item)} ${getCurrencyCodeText(item.currencyCode) || ''}`}
            </SummaryContent.Amount>
          </SummaryContent.Item>
        );
      })}
    </ItemListWrapper>
  );
};

const PackagesList = ({
  checkinDateSelection,
  checkoutDateSelection,
  currentSelectedPackages,
  numberOfGuests,
  isNiagaraProperty,
  upsertPackage,
  deletePackage,
  splitPackagesByTabName,
  allowPackageUpdate,
  triggerDynamicStepNavigation,
  allowPackagesListLink
}) => {
  function updatePackageQuantity(e, packageItem) {
    upsertPackage({ ...packageItem, quantity: e.target.value });
  }

  let groupedPackagesByTabName = null;

  if (splitPackagesByTabName) {
    let groupedPackagesBySectionAndType = Object.groupBy(
      currentSelectedPackages,
      ({ summarySection }) => summarySection
    );

    Object.keys(groupedPackagesBySectionAndType).forEach(summarySectionKey => {
      groupedPackagesBySectionAndType[summarySectionKey] = Object.groupBy(
        groupedPackagesBySectionAndType[summarySectionKey],
        ({ summarySectionType }) => summarySectionType
      );
    });

    groupedPackagesByTabName = groupedPackagesBySectionAndType;
  }

  if (currentSelectedPackages && isArrayEmpty(currentSelectedPackages)) return null;

  const allFoodAndDrinksPackagesAreForCabanaParty = currentSelectedPackages
    .filter(pkg => pkg.summarySection !== 'add-ons')
    .every(pkg => pkg.packageCode.includes('included_food_'));

  const copyForTextLink = summarySection =>
    allFoodAndDrinksPackagesAreForCabanaParty && summarySection !== 'add-ons'
      ? COPY.BIRTHDAY_LINK_TEXT_INCLUDED_FOOD_AND_DRINKS
      : COPY.BIRTHDAY_LINK_TEXT;

  return (
    <>
      {splitPackagesByTabName && groupedPackagesByTabName ? (
        <>
          {Object.entries(groupedPackagesByTabName).map(([summarySection, pkgsList]) => {
            return (
              <>
                <Text fontWeight="bold" fontSize={0} lineHeight={'16px'} capitalize color="deepLakeBlue.100" mb={1}>
                  {summarySection}
                </Text>
                {Object.keys(pkgsList).map(list => (
                  <ListOfPackages
                    key={list}
                    currentSelectedPackages={pkgsList[list]}
                    deletePackage={deletePackage}
                    isNiagaraProperty={isNiagaraProperty}
                    checkinDateSelection={checkinDateSelection}
                    checkoutDateSelection={checkoutDateSelection}
                    numberOfGuests={numberOfGuests}
                    updatePackageQuantity={updatePackageQuantity}
                    allowPackageUpdate={allowPackageUpdate}
                    isBirthdayPackage={splitPackagesByTabName && groupedPackagesByTabName}
                    isFoodAndDrinks={summarySection.includes('&')}
                  />
                ))}

                {allowPackagesListLink ? (
                  <TextLink
                    capitalize
                    lineHeight={16}
                    fontSize={9}
                    fontWeight="bold"
                    onClick={() => {
                      triggerDynamicStepNavigation(
                        FLOW_NAVIGATION_ACTIONS.FLOW_TRIGGER_STEP_BY_SCREEN_TYPE,
                        BIRTHDAY_SECTION_TO_SCREEN_TYPE[summarySection]
                      );
                    }}
                    mt={2}>
                    {editCopyByReplaceString(copyForTextLink(summarySection), summarySection, '<LINK_TEXT>')}
                  </TextLink>
                ) : null}
                <SummaryContent.Divider />
              </>
            );
          })}
        </>
      ) : (
        <>
          <ListOfPackages
            currentSelectedPackages={currentSelectedPackages}
            deletePackage={deletePackage}
            isNiagaraProperty={isNiagaraProperty}
            checkinDateSelection={checkinDateSelection}
            checkoutDateSelection={checkoutDateSelection}
            numberOfGuests={numberOfGuests}
            updatePackageQuantity={updatePackageQuantity}
            allowPackageUpdate={allowPackageUpdate}
          />
          <SummaryContent.Divider />
        </>
      )}
    </>
  );
};

PackagesList.propTypes = propTypes;

PackagesList.defaultProps = {
  isNiagaraProperty: false,
  hasSelectedPackages: false
};

export default PackagesList;
